import classNames from "classnames";
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ViewProps } from "../ExecutiveDashboard";
import Hint from "../components/Hint";
import { Card, Empty, ErrorState, Kpi, Loading, PendingTag, StagePill } from "../components/Visuals";
import { addAreas, area, bothAreas, date, daysFromToday, LEAD_UNIT, NEXT_STEP, number, otherUnit, plural, SCHEDULE_LABELS, scheduleStatus, TRADING_RULE } from "../format";
import { ScheduleStatus, UnitRow, UnitsResponse } from "../types";
import { useDashboardData } from "../useDashboardData";
import { Select } from "./Overview";
import UnitDrawer from "./UnitDrawer";

const STATUS_RULES: { [key in ScheduleStatus]: string } = {
	trading: TRADING_RULE,
	design: "Signed but not open, before the Actual Move in Date.",
	fitout: "Signed but not open, on or after the Actual Move in Date on the Site Handover task.",
	vacant: "No lease holds the unit today.",
	upcoming: "Signed, but the lease has not started and its project has not started yet."
};

const NEXT_SCHEDULE_STEP: { [key in ScheduleStatus]: string } = {
	...NEXT_STEP,
	upcoming: "Next: moves to In Design when its project starts."
};

const TERMINATING_RULE = "A Termination, Contraction or Relocation with a vacate date still to come.";
const FLOATING_RULE = "A Renewal that does not include this unit, so it drops off the lease on the Renewal's start date.";
const PAGE_SIZE = 50;

const AREA_SOURCES: { [source: string]: string } = {
	lease_area: "Source: Lease Area from Yardi unit master data.",
	amendment: "Source: unit area on the lease amendment (no unit master measurement).",
	unit_area: "Source: the unit's stored area (no Yardi measurement).",
	none: "No area recorded for this unit."
};

type Mode = "terminations" | "schedule" | "vacant";

interface Props extends ViewProps {
	mode: Mode;
}

const Units = ({ mode, asset, properties }: Props) => {
	const { data, isLoading, error } = useDashboardData<UnitsResponse>("units", { property_id: asset ? asset.id : undefined });
	const [query, setQuery] = useState("");
	const location = useLocation();
	const [status, setStatus] = useState(() => new URLSearchParams(location.search).get("status") || "");
	const [flag, setFlag] = useState("");
	const [reason, setReason] = useState("");
	const [property, setProperty] = useState("");
	const [limit, setLimit] = useState(PAGE_SIZE);
	const [openUnit, setOpenUnit] = useState<string | null>(null);

	if (isLoading) return <Loading />;
	if (error || !data) return <ErrorState />;

	const terminations = mode === "terminations";
	const vacantMode = mode === "vacant";
	const needle = query.trim().toLowerCase();
	const scoped = property ? data.units.filter((unit) => unit.property.id === property) : data.units;
	const base = terminations
		? scoped.filter((unit) => unit.vacating && unit.vacating.kind === "terminating")
		: vacantMode
		? scoped.filter((unit) => unit.status === "vacant")
		: scoped;
	const rows = base
		.filter((unit) => {
			if (status && scheduleStatus(unit) !== status) return false;
			if (reason && (!unit.vacating || unit.vacating.reason !== reason)) return false;
			if (flag === "terminating" && !(unit.vacating && unit.vacating.kind === "terminating")) return false;
			if (flag === "floating" && !(unit.vacating && unit.vacating.kind === "floating")) return false;
			if (flag === "none" && unit.vacating) return false;
			if (!needle) return true;
			return [unit.code, unit.name, unit.tenant_name, unit.zone, unit.sub_zone].join(" ").toLowerCase().includes(needle);
		})
		.sort(terminations ? byVacateDate : vacantMode ? byVacantSince : () => 0);

	const reasons = Object.keys(data.summary.by_reason).sort();
	const upcoming90 = base.filter((unit) => {
		const days = unit.vacating ? daysFromToday(unit.vacating.date, data.as_of) : null;
		return days !== null && days <= 90;
	}).length;
	const terminatingUnits = base.filter((unit) => unit.vacating && unit.vacating.kind === "terminating");
	const terminatingGla = addAreas(terminatingUnits.map((unit) => unit.gla));
	const terminatingCount = terminatingUnits.length;
	const vacantGla = addAreas(base.map((unit) => unit.gla));
	const previouslyLeased = base.filter((unit) => unit.vacant_since);
	const neverLeased = base.filter((unit) => !unit.vacant_since);

	return (
		<div className="ed-stack">
			<div className="ed-kpis">
				{vacantMode ? (
					<React.Fragment>
						<Kpi
							label="Vacant units"
							value={number(base.length)}
							valueTone="vacant"
							explain={{
								title: "Vacant units",
								lines: [STATUS_RULES.vacant, "Units signed but not yet open (Design or Fit-out) are not vacant.", "The same units as Vacant on the Overview, counted individually."]
							}}
						/>
						<Kpi
							label="Vacant GLA"
							value={area(vacantGla)}
							sub={area(vacantGla, otherUnit(LEAD_UNIT))}
							explain={{ title: "Vacant GLA", lines: ["Lease area of the vacant units.", bothAreas(vacantGla)] }}
						/>
						<Kpi
							label="Previously leased"
							dot="vacant"
							value={number(previouslyLeased.length)}
							sub={area(addAreas(previouslyLeased.map((unit) => unit.gla)))}
							explain={{ title: "Previously leased", lines: ["Vacant units with an earlier lease on record, so a known vacant-since date."] }}
						/>
						<Kpi
							label="No lease on record"
							dot="neutral"
							value={number(neverLeased.length)}
							sub={area(addAreas(neverLeased.map((unit) => unit.gla)))}
							explain={{ title: "No lease on record", lines: ["Vacant units with no earlier lease in TCP or Yardi."] }}
						/>
					</React.Fragment>
				) : terminations ? (
					<React.Fragment>
						<Kpi
							label="Terminating units"
							value={number(data.summary.terminating)}
							valueTone="vacant"
							explain={{ title: "Terminating units", lines: [TERMINATING_RULE] }}
						/>
						<Kpi
							label="Vacating in 90 days"
							dot="vacant"
							value={number(upcoming90)}
							explain={{ title: "Vacating in 90 days", lines: ["Terminating units whose vacate date is within the next 90 days."] }}
						/>
						<Kpi
							label="GLA terminating"
							value={area(terminatingGla)}
							sub={area(terminatingGla, otherUnit(LEAD_UNIT))}
							explain={{
								title: "GLA terminating",
								lines: [
									"Sum of Lease Area SQFT and Lease Area SQM (Yardi unit master data) for the terminating units, as in force today.",
									`${plural(terminatingCount, "unit")}: ${bothAreas(terminatingGla)}`
								]
							}}
						/>
						<Kpi
							label="By reason"
							value={
								reasons.length ? (
									<span className="ed-kpi__list">
										{reasons.map((key) => (
											<span key={key}>
												<strong>{data.summary.by_reason[key]}</strong> {key}
											</span>
										))}
									</span>
								) : (
									"–"
								)
							}
							explain={{ title: "By reason", lines: ["The amendment type that vacates the unit."] }}
						/>
					</React.Fragment>
				) : (
					<React.Fragment>
						<Kpi label="Units" value={number(data.summary.total)} explain={{ title: "Units", lines: ["Every counted unit at the asset(s) in view."] }} />
						<Kpi
							label="Trading"
							dot="trading"
							value={number(data.summary.by_status.trading || 0)}
							valueTone="trading"
							explain={{ title: "Trading", lines: [STATUS_RULES.trading] }}
						/>
						<Kpi
							label="Terminating"
							dot="vacant"
							value={number(data.summary.terminating)}
							valueTone="vacant"
							explain={{ title: "Terminating", lines: [TERMINATING_RULE] }}
						/>
						<Kpi
							label="Floating"
							dot="refurb"
							value={number(data.summary.floating)}
							valueTone="refurb"
							explain={{ title: "Floating", lines: [FLOATING_RULE] }}
						/>
					</React.Fragment>
				)}
			</div>

			<div className="ed-toolbar ed-toolbar--wrap">
				<input
					className="ed-search"
					type="search"
					placeholder="Search unit, tenant, zone…"
					value={query}
					onChange={(event) => {
						setQuery(event.target.value);
						setLimit(PAGE_SIZE);
					}}
				/>
				{!asset && (
					<Select
						label="Property"
						value={property}
						options={properties.map((item) => item.id)}
						format={(id) => {
							const match = properties.find((item) => item.id === id);
							return match ? match.short_name : id;
						}}
						onChange={(value) => {
							setProperty(value);
							setLimit(PAGE_SIZE);
						}}
					/>
				)}
				{!vacantMode && (
					<Select
						label="Status"
						value={status}
						options={["trading", "upcoming", "design", "fitout", "vacant"]}
						format={(value) => SCHEDULE_LABELS[value as ScheduleStatus]}
						onChange={setStatus}
					/>
				)}
				{vacantMode ? null : terminations ? (
					<Select label="Reason" value={reason} options={reasons} onChange={setReason} />
				) : (
					<Select
						label="Flag"
						value={flag}
						options={["terminating", "floating", "none"]}
						format={(value) => (value === "terminating" ? "Terminating" : value === "floating" ? "Floating" : "Neither")}
						onChange={setFlag}
					/>
				)}
			</div>

			<Card
				title={terminations ? "Upcoming vacancies" : vacantMode ? "Vacant units" : "All units"}
				subtitle={`${plural(rows.length, "unit")}${terminations ? ", soonest first" : vacantMode ? ", most recently vacated first" : ""}`}
				className="ed-card--flush"
			>
				{rows.length === 0 ? (
					<Empty>{terminations ? "No units have a termination coming up." : vacantMode ? "No vacant units match." : "No units match."}</Empty>
				) : (
					<div className="ed-table-wrap">
						<table className="ed-table">
							<thead>
								<tr>
									<th>Unit</th>
									{!asset && <th>Property</th>}
									{!vacantMode && <th>Tenant</th>}
									<th>Zone</th>
									<th className="ed-num">GLA</th>
									{vacantMode ? (
										<th>Vacant since</th>
									) : (
										<React.Fragment>
											<th>Lease start</th>
											<th>Lease end</th>
										</React.Fragment>
									)}
									<th>Status</th>
									{!vacantMode && <th>{terminations ? "Vacates" : "Flag"}</th>}
								</tr>
							</thead>
							<tbody>
								{rows.slice(0, limit).map((unit) => (
									<UnitLine key={unit.id} unit={unit} today={data.as_of} showProperty={!asset} onOpen={setOpenUnit} mode={mode} />
								))}
							</tbody>
						</table>
					</div>
				)}
				{rows.length > limit && (
					<div className="ed-more">
						<button type="button" className="ed-button ed-button--ghost" onClick={() => setLimit(limit + PAGE_SIZE)}>
							Show {Math.min(PAGE_SIZE, rows.length - limit)} more of {number(rows.length - limit)}
						</button>
					</div>
				)}
			</Card>
			{openUnit && <UnitDrawer unitId={openUnit} onClose={() => setOpenUnit(null)} />}
		</div>
	);
};

interface LineProps {
	unit: UnitRow;
	today: string;
	showProperty: boolean;
	onOpen: (id: string) => void;
	mode: Mode;
}

const UnitLine = ({ unit, today, showProperty, onOpen, mode }: LineProps) => {
	const vacantMode = mode === "vacant";
	const days = unit.vacating ? daysFromToday(unit.vacating.date, today) : null;
	const shown = scheduleStatus(unit);
	return (
		<tr>
			<td className="ed-strong">
				<button type="button" className="ed-unit-link" onClick={() => onOpen(unit.id)} title="Show lease history">
					{unit.code}
				</button>
			</td>
			{showProperty && <td>{unit.property.short_name}</td>}
			{!vacantMode && (
				<td>
					{unit.project_id ? (
						<Link to={`/projects/${unit.project_id}`} className="ed-link ed-link--plain">
							{unit.tenant_name || "–"}
						</Link>
					) : (
						unit.tenant_name || <span className="ed-muted">–</span>
				)}
				{unit.project_pending && <PendingTag />}
			</td>
			)}
			<td>
				{unit.zone || "–"}
				{unit.sub_zone && <div className="ed-subtext">{unit.sub_zone}</div>}
			</td>
			<td className="ed-num">
				<Hint align="right" explain={{ lines: [bothAreas(unit.gla), AREA_SOURCES[unit.area_source || "none"]] }}>
					{area(unit.gla)}
					<div className="ed-subtext">{area(unit.gla, otherUnit(LEAD_UNIT))}</div>
				</Hint>
			</td>
			{vacantMode ? (
				<td>
					{unit.vacant_since ? (
						<React.Fragment>
							{date(unit.vacant_since)}
							<div className="ed-subtext">{plural(-(daysFromToday(unit.vacant_since, today) || 0), "day")} ago</div>
						</React.Fragment>
					) : (
						<span className="ed-muted">No lease on record</span>
					)}
				</td>
			) : (
				<React.Fragment>
					<td>{date(unit.lease_start)}</td>
					<td>{date(unit.lease_end)}</td>
				</React.Fragment>
			)}
			<td>
				<Hint
					explain={{
						lines: [
							STATUS_RULES[shown],
							unit.move_in_actual && unit.move_in_date ? `Actual Move in Date: ${date(unit.move_in_date)}` : null,
							unit.target_open_date ? `Target open: ${date(unit.target_open_date)}` : null,
							NEXT_SCHEDULE_STEP[shown]
						]
					}}
				>
					<StagePill stage={shown} label={SCHEDULE_LABELS[shown]} />
				</Hint>
			</td>
			{!vacantMode && (
				<td>
					{unit.vacating ? (
						<Hint
							align="right"
							explain={{
								title: unit.vacating.kind === "terminating" ? "Terminating" : "Floating",
								lines: [unit.vacating.kind === "terminating" ? TERMINATING_RULE : FLOATING_RULE, `Source: ${unit.vacating.reason} amendment`]
							}}
						>
							<span className={classNames("ed-flag", `ed-flag--${unit.vacating.kind}`)}>
								{unit.vacating.kind === "terminating" ? unit.vacating.reason : "Floating"}
							</span>
							<div className="ed-subtext">
								{date(unit.vacating.date)}
								{days !== null ? ` · in ${days}d` : ""}
							</div>
						</Hint>
					) : (
						<span className="ed-muted">–</span>
					)}
				</td>
			)}
		</tr>
	);
};

const byVacantSince = (a: UnitRow, b: UnitRow) =>
	(b.vacant_since || "").localeCompare(a.vacant_since || "") ||
	a.property.name.localeCompare(b.property.name) ||
	a.code.localeCompare(b.code, undefined, { numeric: true });

const byVacateDate = (a: UnitRow, b: UnitRow) => (a.vacating ? a.vacating.date : "").localeCompare(b.vacating ? b.vacating.date : "");

export default Units;
